/*
 * autoScanRunner.ts — Auto Scan's album sweep as a page-independent job.
 *
 * Same shape as myPokemonImportRunner (the Poké Genie import): any page can
 * start it, the global AutoScanBar (mounted in App) shows progress wherever
 * the trainer navigates, and closing the Upload menu changes nothing.
 * Classify + save go through screenshotScanPipeline — the SAME core the
 * upload modal uses, so the two paths cannot drift.
 *
 * What it saves on its own: every row the pipeline is certain of. Rows that
 * need a human (uncertain variant, unresolved nickname, Pro-locked detail
 * page) are HELD by asset id (screenshotLibrary's held queue) for the Upload
 * menu's review entry — never guessed at, never silently dropped.
 *
 * Scope honesty: "background" means WITHIN the app. When iOS suspends the
 * webview, all JS stops with it. The sweep is resumable though: the cursor
 * advances per screenshot, and re-running is idempotent (content-hash dedupe
 * + additive writes), so an interrupted run just continues next tap.
 */
import { useSyncExternalStore } from "react";

import {
  addHeldScreenshot,
  ensureScreenshotAccess,
  planSweep,
  readScreenshotFile,
  setSweepCursor,
} from "./screenshotLibrary";
import {
  classifyScreenshot,
  needsHumanReview,
  saveScreenshotRows,
  type Pending,
  type SaveSummary,
} from "./screenshotScanPipeline";

export type AutoScanPhase = "idle" | "scanning" | "saving" | "done" | "error";

/**
 * Why an error happened, when the UI can act on it: "photos-denied" gets an
 * Open Settings action (the Photos toggle exists there), "photos-restricted"
 * does not (Screen Time / parental controls: Settings has no Photos row for
 * the app, so sending people there is a dead end).
 */
export type AutoScanErrorKind = "photos-denied" | "photos-restricted" | "generic";

export interface AutoScanState {
  phase: AutoScanPhase;
  /** Screenshots processed so far / planned, while scanning. */
  done: number;
  total: number;
  /** Photos that were not Pokémon GO screens (or unreadable). */
  skipped: number;
  /** Rows held for human review (see the Upload menu's review entry). */
  held: number;
  summary: SaveSummary | null;
  error: string | null;
  errorKind: AutoScanErrorKind | null;
}

const IDLE: AutoScanState = {
  phase: "idle",
  done: 0,
  total: 0,
  skipped: 0,
  held: 0,
  summary: null,
  error: null,
  errorKind: null,
};

let state: AutoScanState = IDLE;
let running = false;
let hideTimer: number | null = null;
const listeners = new Set<() => void>();

function setState(next: AutoScanState): void {
  state = next;
  for (const l of listeners) l();
}

/** Clear a finished (done/error) banner. No-op mid-run. */
export function dismissAutoScan(): void {
  if (state.phase === "scanning" || state.phase === "saving") return;
  if (hideTimer != null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  setState(IDLE);
}

function autoHide(ms: number): void {
  if (hideTimer != null) clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    hideTimer = null;
    dismissAutoScan();
  }, ms) as unknown as number;
}

function fail(error: string, errorKind: AutoScanErrorKind = "generic"): void {
  setState({ ...IDLE, phase: "error", error, errorKind });
  // Actionable permission errors stay up until dismissed; transient ones
  // clear themselves.
  if (errorKind === "generic") autoHide(8000);
}

export interface StartAutoScanOptions {
  /** Gates detail-page rows exactly as the upload modal does. */
  isPro: boolean;
  /**
   * The on-open sweep (autoScanOnOpen.ts). Nobody tapped anything, so a
   * quiet run must not answer with a banner unless it has something to say:
   * no permission errors (the caller already checked the grant without
   * prompting), no "no new GO screenshots" line, no "album unreadable" line.
   * Once there IS something to scan the bar shows exactly as a manual run.
   */
  quiet?: boolean;
}

/**
 * Start the sweep. Returns false when one is already running or photo access
 * is denied (the caller falls back to the picker path). isPro gates
 * detail-page rows exactly as the modal does.
 */
export async function startAutoScan(opts: StartAutoScanOptions): Promise<boolean> {
  if (running) return false;
  running = true;
  const quiet = opts.quiet === true;
  try {
    const status = await ensureScreenshotAccess();
    if (status === "denied") {
      if (!quiet) {
        fail(
          "Photos access is off. Allow it in Settings, or use Select screenshots.",
          "photos-denied",
        );
      }
      return false;
    }
    if (status === "restricted") {
      if (!quiet) {
        fail(
          "Photos access is blocked by Screen Time or parental controls on this device. Select screenshots still works.",
          "photos-restricted",
        );
      }
      return false;
    }
    let plan;
    try {
      plan = await planSweep();
    } catch {
      if (!quiet) fail("Couldn't read your Screenshots album. Try again.");
      return true;
    }
    if (plan.items.length === 0) {
      if (quiet) return true;
      // Limited access with nothing shared looks identical to "no new
      // screenshots" from here; name the real cause so the trainer knows
      // the fix is in Settings, not in taking more screenshots.
      if (status === "limited" && plan.total === 0) {
        // Same affordance as denied: the Photos row exists in Settings
        // (showing Limited), so the Open Settings action helps here too.
        fail(
          "Photos access is limited and no screenshots are shared. Allow more in Settings, or use Select screenshots.",
          "photos-denied",
        );
        return true;
      }
      setState({ ...IDLE, phase: "done", summary: null });
      autoHide(5000);
      return true;
    }
    setState({ ...IDLE, phase: "scanning", total: plan.items.length });

    const clean: Pending[] = [];
    let skipped = 0;
    let held = 0;
    for (let i = 0; i < plan.items.length; i++) {
      const item = plan.items[i];
      try {
        const file = await readScreenshotFile(item, i);
        const res = await classifyScreenshot(file, { isPro: opts.isPro });
        if (res.kind === "skip") {
          skipped += 1;
        } else if (needsHumanReview(res.pending)) {
          addHeldScreenshot(item);
          held += 1;
        } else {
          clean.push(res.pending);
        }
      } catch (err) {
        console.warn("[autoScanRunner] screenshot failed, skipping", err);
        skipped += 1;
      }
      // Per-item cursor advance: an interrupted run resumes where it left off.
      setSweepCursor(item.createdAt);
      if (state.phase === "scanning") {
        setState({ ...state, done: i + 1, skipped, held });
      }
    }

    setState({ ...state, phase: "saving", skipped, held });
    const summary =
      clean.length > 0 ? await saveScreenshotRows(clean, "auto") : null;
    setState({
      phase: "done",
      done: plan.items.length,
      total: plan.items.length,
      skipped,
      held,
      summary,
      error: null,
      errorKind: null,
    });
    autoHide(10000);
    return true;
  } catch (err) {
    console.warn("[autoScanRunner] sweep failed", err);
    fail("Auto Scan hit a problem. Try again.");
    return true;
  } finally {
    running = false;
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot(): AutoScanState {
  return state;
}
function getServerSnapshot(): AutoScanState {
  return IDLE;
}

/** Live sweep state, for the global AutoScanBar and the Upload menu. */
export function useAutoScan(): AutoScanState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Test seam. */
export function __resetAutoScanForTest(): void {
  running = false;
  if (hideTimer != null) clearTimeout(hideTimer);
  hideTimer = null;
  state = IDLE;
}
